import { useEffect, useRef } from 'react';

import { TransProgressKind } from './TransProgressExternal';
import { TransProgressFile, TransProgressStatus } from './types';

type TransProgressAriaProps = {
  files: TransProgressFile[];
};

export const TransProgressAria = ({ files }: TransProgressAriaProps) => {
  const previousActiveCountRef = useRef<number>(0);
  const completionAnnouncedRef = useRef<boolean>(false);

  useEffect(() => {
    const activeFiles = files.filter(
      file =>
        file.status === TransProgressStatus.STARTING ||
        file.status === TransProgressStatus.TRANSFERING
    );

    const previousActiveCount = previousActiveCountRef.current;
    const currentActiveCount = activeFiles.length;

    // Se tinha transferências ativas e agora não tem mais, anuncia conclusão
    if (
      previousActiveCount > 0 &&
      currentActiveCount === 0 &&
      files.length > 0
    ) {
      completionAnnouncedRef.current = true;
    } else if (currentActiveCount > 0) {
      // Reseta o flag se há transferências ativas novamente
      completionAnnouncedRef.current = false;
    }

    previousActiveCountRef.current = currentActiveCount;
  }, [files]);

  const renderActiveTransfers = () => {
    const activeFiles = files.filter(
      file =>
        file.status === TransProgressStatus.STARTING ||
        file.status === TransProgressStatus.TRANSFERING
    );

    if (activeFiles.length === 0) {
      return null;
    }

    const uploading = activeFiles.filter(
      file => file.kind === TransProgressKind.UPLOAD
    ).length;
    const downloading = activeFiles.filter(
      file => file.kind !== TransProgressKind.UPLOAD
    ).length;

    const parts: string[] = [];
    if (uploading > 0) {
      parts.push(
        `${uploading} arquivo${uploading > 1 ? 's' : ''} sendo enviado${
          uploading > 1 ? 's' : ''
        }`
      );
    }
    if (downloading > 0) {
      parts.push(
        `${downloading} arquivo${downloading > 1 ? 's' : ''} sendo baixado${
          downloading > 1 ? 's' : ''
        }`
      );
    }

    return (
      <div role="alert" aria-live="polite" aria-atomic="true">
        {parts.join(' e ')}
      </div>
    );
  };

  const renderCompletion = () => {
    const activeFiles = files.filter(
      file =>
        file.status === TransProgressStatus.STARTING ||
        file.status === TransProgressStatus.TRANSFERING
    );

    const hasActiveTransfers = activeFiles.length > 0;
    const shouldAnnounceCompletion =
      completionAnnouncedRef.current && !hasActiveTransfers;

    if (!shouldAnnounceCompletion) {
      return null;
    }

    return (
      <div role="alert" aria-live="polite" aria-atomic="true">
        Todas as transferências foram concluídas
      </div>
    );
  };

  const renderSrFails = () => {
    const failedFiles = files.filter(
      file => file.status === TransProgressStatus.FAILED
    );
    return failedFiles.map(file => (
      <div key={file.id} role="alert" aria-live="assertive">
        {`Falha no ${
          file.kind === TransProgressKind.UPLOAD ? 'envio' : 'download'
        } do arquivo ${file.name}: ${file.statusInfo}`}
      </div>
    ));
  };

  return (
    <div>
      {renderSrFails()}
      {renderActiveTransfers()}
      {renderCompletion()}
    </div>
  );
};
